import React, { useState } from 'react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Lead } from '../../types/lead.types';
import { useAuth } from '../../contexts/AuthContext';

interface BulkActionsBarProps {
  selectedCount: number;
  onBulkUpdate: (updates: any) => Promise<void>;
  onBulkDelete: () => Promise<void>;
  onDeselectAll: () => void;
}

const BulkActionsBar: React.FC<BulkActionsBarProps> = ({
  selectedCount,
  onBulkUpdate,
  onBulkDelete,
  onDeselectAll
}) => {
  const { userProfile } = useAuth();
  const [isUpdating, setIsUpdating] = useState(false);

  // Field users cannot update determination
  const canEditDetermination = userProfile?.role !== 'field';

  const handleStatusUpdate = async (status: string) => {
    setIsUpdating(true);
    try {
      await onBulkUpdate({ status });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeterminationUpdate = async (determination: string) => {
    setIsUpdating(true);
    try {
      await onBulkUpdate({ determination });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm(`Are you sure you want to delete ${selectedCount} lead(s)?`)) {
      setIsUpdating(true);
      try {
        await onBulkDelete();
      } finally {
        setIsUpdating(false);
      }
    }
  };

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center">
            <CheckIcon className="h-5 w-5 text-blue-600 mr-2" />
            <span className="text-sm font-medium text-blue-900">
              {selectedCount} lead{selectedCount !== 1 ? 's' : ''} selected
            </span>
          </div>

          {/* Status Updates */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-blue-700">Update status:</span>
            <select
              onChange={(e) => e.target.value && handleStatusUpdate(e.target.value)}
              disabled={isUpdating}
              className="text-sm border-blue-300 rounded focus:ring-blue-500 focus:border-blue-500"
              defaultValue=""
            >
              <option value="">Choose status...</option>
              <option value="cold">Cold</option>
              <option value="warm">Warm</option>
              <option value="hot">Hot</option>
              <option value="enrolled">Enrolled</option>
              <option value="dnc">Do Not Contact</option>
              <option value="lost">Lost</option>
            </select>
          </div>

          {/* Determination Updates - Hidden for field users */}
          {canEditDetermination && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-blue-700">Mark as:</span>
              <button
                onClick={() => handleDeterminationUpdate('valid')}
                disabled={isUpdating}
                className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded hover:bg-green-200 disabled:opacity-50"
              >
                Valid
              </button>
              <button
                onClick={() => handleDeterminationUpdate('invalid')}
                disabled={isUpdating}
                className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded hover:bg-red-200 disabled:opacity-50"
              >
                Invalid
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={handleDelete}
            disabled={isUpdating}
            className="px-3 py-1 text-sm bg-red-100 text-red-800 rounded hover:bg-red-200 disabled:opacity-50"
          >
            Delete
          </button>
          <button
            onClick={onDeselectAll}
            disabled={isUpdating}
            className="text-blue-600 hover:text-blue-800 disabled:opacity-50"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {isUpdating && (
        <div className="mt-2 flex items-center text-sm text-blue-600">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
          Updating leads...
        </div>
      )}
    </div>
  );
};

export default BulkActionsBar;
